import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { AssignmentAreaPicker, DateRangeFields } from "./AssignmentAreaPicker";
import { todayStr, mondayOf, addDaysStr, WEEKDAY_LABELS, MAX_SPAN_DAYS } from "./assignmentConstants";

// The logged-in staff member's own assignment — shown at login (compact: just today)
// and from the user-menu "My Assignment" entry (full: today + a Mon-Sun weekly
// schedule, incl. shift name/time when a supervisor set one). Read-only under
// Supervisor mode; full self-serve (add/remove, change mid-shift) under Self mode.
export function MyAssignmentPanel({ compact = false }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [units, setUnits] = useState([]);
    const [adding, setAdding] = useState(false);
    const [areas, setAreas] = useState([{ area_type: "unit", area_value: "" }]);
    const [startDate, setStartDate] = useState(todayStr());
    const [endDate, setEndDate] = useState(todayStr());
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/institution/staff-assignments/mine"),
            api.get("/institution/units").catch(() => ({ data: { units: [] } })),
        ]).then(([mine, u]) => {
            setData(mine.data);
            setUnits(u.data?.units || []);
        }).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const removeRow = async (id) => {
        try {
            await api.delete(`/institution/staff-assignments/${id}`);
            load();
        } catch {
            toast.error("Could not remove that assignment.");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/institution/staff-assignments/self-assign", { areas, start_date: startDate, end_date: endDate });
            toast.success("Your assignment is set.");
            setAdding(false);
            setAreas([{ area_type: "unit", area_value: "" }]);
            setStartDate(todayStr()); setEndDate(todayStr());
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save your assignment.");
        } finally { setSaving(false); }
    };

    if (loading) return <div data-testid="my-assignment-loading" className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
    if (!data) return null;
    const isSelf = data.mode === "self";

    return (
        <div data-testid="my-assignment-panel" className="space-y-3">
            <div>
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Today</div>
                {data.today.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="my-assignment-none-today">
                        {isSelf ? "No assignment set for today yet." : "No assignment set for you today — check with your supervisor."}
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="my-assignment-today-chips">
                        {data.today.map((a) => {
                            const isUnassigned = a.source === "unassigned";
                            return (
                                <span key={a.id} data-testid={isUnassigned ? "my-assignment-unassigned-chip" : undefined} className={
                                    isUnassigned
                                        ? "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border border-dashed border-amber-400 text-amber-700"
                                        : "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-phoenix-navy text-white"
                                }>
                                    {a.area_label}{a.shift_label && ` · ${a.shift_label}`}{isUnassigned && " · not pre-assigned"}
                                    {isSelf && !isUnassigned && (
                                        <button data-testid={`my-assignment-remove-${a.id}`} onClick={() => removeRow(a.id)} className="hover:text-red-300">
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            {!compact && <MyWeekSchedule onRemove={removeRow} isSelf={isSelf} />}

            {isSelf && (
                adding ? (
                    <div className="border border-border rounded-lg p-3 space-y-2 bg-secondary/20" data-testid="my-assignment-form">
                        <AssignmentAreaPicker areas={areas} setAreas={setAreas} units={units} testIdPrefix="my-assign" />
                        <DateRangeFields startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
                            maxSpanDays={MAX_SPAN_DAYS} testIdPrefix="my-assign" />
                        <div className="flex items-center gap-2">
                            <Button size="sm" data-testid="my-assignment-save" disabled={saving} onClick={save}>
                                {saving ? "Saving…" : "Save"}
                            </Button>
                            <Button size="sm" variant="ghost" data-testid="my-assignment-cancel" onClick={() => setAdding(false)}>Cancel</Button>
                        </div>
                    </div>
                ) : (
                    <Button size="sm" variant="outline" data-testid="my-assignment-add-btn" onClick={() => setAdding(true)}>
                        {data.today.length > 0 ? "Change my assignment" : "Set my assignment"}
                    </Button>
                )
            )}
        </div>
    );
}

// Mon-Sun view of the staff member's own schedule, with Prev/Next week nav — same
// week-at-a-glance idea as the supervisor's Coverage grid, scoped to just this person.
function MyWeekSchedule({ onRemove, isSelf }) {
    const [weekStart, setWeekStart] = useState(mondayOf());
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setData(null); setError(false);
        api.get("/institution/staff-assignments/mine-week", { params: { week_start: weekStart } })
            .then((r) => setData(r.data)).catch(() => setError(true));
    }, [weekStart]);

    const today = todayStr();

    return (
        <div data-testid="my-assignment-week" className="space-y-2">
            <div className="flex items-center justify-between">
                <button data-testid="my-assignment-week-prev" onClick={() => setWeekStart(addDaysStr(weekStart, -7))}
                    className="text-xs font-semibold text-phoenix-navy hover:underline">‹ Prev</button>
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">This week</div>
                <button data-testid="my-assignment-week-next" onClick={() => setWeekStart(addDaysStr(weekStart, 7))}
                    className="text-xs font-semibold text-phoenix-navy hover:underline">Next ›</button>
            </div>
            {error ? (
                <p className="text-sm text-destructive">Couldn't load your weekly schedule.</p>
            ) : !data ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (
                <div className="border border-border rounded-lg divide-y divide-border/60" data-testid="my-assignment-week-list">
                    {data.days.map((day, i) => (
                        <div key={day.date} className={`px-2.5 py-1.5 flex items-start justify-between gap-2 ${day.date === today ? "bg-secondary/30" : ""}`}>
                            <div className="text-xs font-semibold text-phoenix-navy w-16 shrink-0 pt-0.5">
                                {WEEKDAY_LABELS[i]} <span className="text-muted-foreground font-normal">{day.date.slice(5)}</span>
                            </div>
                            {day.assignments.length === 0 ? (
                                <div className="text-xs text-muted-foreground flex-1 text-right pt-0.5">Not assigned</div>
                            ) : (
                                <div className="flex-1 flex flex-wrap justify-end gap-1">
                                    {day.assignments.map((a, ai) => {
                                        const isUnassigned = a.source === "unassigned";
                                        return (
                                            <span key={ai} className={
                                                isUnassigned
                                                    ? "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold border border-dashed border-amber-400 text-amber-700"
                                                    : "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-phoenix-navy/10 text-phoenix-navy"
                                            }>
                                                {a.area_label}{a.shift_label && ` · ${a.shift_label}`}
                                                {a.shift_time && <span className="text-muted-foreground font-normal ml-1">({a.shift_time})</span>}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
